/** Meetings and the task board, as the screens see it (BR-26, P4-BR3). */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../../api/client.js';

export interface Attendee {
  id: number;
  fullName: string;
}

export interface Meeting {
  id: number;
  date: string;
  decisions: string;
  attendees: Attendee[];
  linkedBudgetId: number | null;
  note: string | null;
  recordedById: number | null;
  createdAt: string;
}

export interface Task {
  id: number;
  title: string;
  assignedToId: number | null;
  assignedToName: string | null;
  dueDate: string | null;
  status: 'pending' | 'done' | 'cancelled';
  doneAt: string | null;
  doneById: number | null;
  cancelReason: string | null;
  note: string | null;
  createdById: number | null;
  isOverdue: boolean;
}

interface Envelope<T> {
  success: true;
  data: T;
}

export const governanceKeys = {
  meetings: () => ['meetings'] as const,
  meeting: (id: number) => ['meetings', id] as const,
  tasks: (status?: string) => ['tasks', status ?? 'all'] as const,
};

export function useMeetings(): UseQueryResult<Meeting[]> {
  return useQuery({
    queryKey: governanceKeys.meetings(),
    queryFn: async () => (await api.get<Envelope<Meeting[]>>('/meetings')).data.data,
  });
}

export function useTasks(status?: 'pending' | 'done' | 'cancelled'): UseQueryResult<Task[]> {
  return useQuery({
    queryKey: governanceKeys.tasks(status),
    queryFn: async () =>
      (await api.get<Envelope<Task[]>>('/tasks', status ? { params: { status } } : undefined)).data.data,
  });
}

export interface MeetingInput {
  date: string;
  attendees: number[];
  decisions: string;
  note?: string;
}

export function useCreateMeeting(): UseMutationResult<Meeting, unknown, MeetingInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: MeetingInput) => (await api.post<Envelope<Meeting>>('/meetings', input)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: governanceKeys.meetings() });
    },
  });
}

export interface TaskInput {
  title: string;
  assignedToId?: number;
  dueDate?: string;
}

export function useCreateTask(): UseMutationResult<Task, unknown, TaskInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TaskInput) => (await api.post<Envelope<Task>>('/tasks', input)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/** P4-BR3 — the server decides whether this is allowed; the screen only asks. */
export function useCompleteTask(): UseMutationResult<Task, unknown, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: number) => (await api.post<Envelope<Task>>(`/tasks/${taskId}/complete`)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useReopenTask(): UseMutationResult<Task, unknown, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: number) => (await api.post<Envelope<Task>>(`/tasks/${taskId}/reopen`)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/** One field that moved, already formatted and already redacted (SEC-9). */
export interface AuditFieldChange {
  field: string;
  label: string;
  from: string;
  to: string;
}

export interface AuditEntry {
  at: string;
  actorName: string | null;
  action: string;
  table: string;
  /**
   * What to call the table on screen, sent by the server.
   *
   * Not looked up from a client i18n key any more. That second catalogue
   * drifted from the server's — it knew 14 tables to the server's 8 while 28
   * write audit rows — so entries rendered as the literal missing key,
   * "audit.table.stock_movements #1". One catalogue cannot disagree with
   * itself.
   */
  tableLabel: string;
  recordId: number;
  /** Pre-phrased by the server in the partner's language (P4-TR4). */
  summary: string;
  /** The same change field by field, for the expanded row. */
  changes: AuditFieldChange[];
  /** Where it came from, and the thread back to the server logs (§12.4). */
  ip: string | null;
  requestId: string | null;
}

/** The tables a partner may filter by, labelled by the server (NFR-6). */
export function useAuditTables(): UseQueryResult<{ name: string; label: string }[]> {
  return useQuery({
    queryKey: ['audit', 'tables'],
    queryFn: async () =>
      (
        await api.get<Envelope<{ name: string; label: string }[]>>('/audit/tables', {
          params: { locale: document.documentElement.lang || 'bn' },
        })
      ).data.data,
    // Reference data: the list changes when the schema does, not in a session.
    staleTime: Infinity,
  });
}

/**
 * The activity feed (P4-BR2).
 *
 * The locale goes to the server because the sentences are composed there —
 * the browser never receives the raw before/after JSON, which keeps SEC-9
 * redaction in one place.
 */
export function useAuditFeed(table?: string): UseQueryResult<AuditEntry[]> {
  return useQuery({
    queryKey: ['audit', table ?? 'all'],
    queryFn: async () =>
      (
        await api.get<Envelope<AuditEntry[]>>('/audit', {
          params: { ...(table !== undefined ? { table } : {}), locale: document.documentElement.lang || 'bn' },
        })
      ).data.data,
  });
}

/** One record's whole story, oldest first (§12.4). */
export function useAuditHistory(table: string, recordId: number): UseQueryResult<AuditEntry[]> {
  return useQuery({
    queryKey: ['audit', table, recordId],
    queryFn: async () =>
      (
        await api.get<Envelope<AuditEntry[]>>(`/audit/${table}/${recordId}`, {
          params: { locale: document.documentElement.lang || 'bn' },
        })
      ).data.data,
  });
}

export interface SeasonAnimal {
  animalId: number;
  tag: string;
  askingPrice: string | null;
  bookingStatus: 'booked' | 'delivered' | 'cancelled' | null;
  customerName: string | null;
  currentWeightKg: string;
  dailyGainKg: string | null;
  daysRemaining: number;
  projectedWeightKg: string | null;
  targetWeightKg: string | null;
  willMissTarget: boolean;
  shortfallKg: string | null;
}

export interface SeasonView {
  eidDate: string;
  daysRemaining: number;
  isOver: boolean;
  animals: SeasonAnimal[];
  atRiskCount: number;
  unbookedCount: number;
  pnl: {
    animalsSold: number;
    revenue: string;
    cost: string;
    profit: string;
    marginPercent: string | null;
    averagePricePerAnimal: string | null;
  };
}

/** The Eid countdown, weight trajectories and season P&L (§12.1 item 4). */
export function useQurbaniSeason(eidDate: string): UseQueryResult<SeasonView> {
  return useQuery({
    queryKey: ['qurbani-season', eidDate],
    queryFn: async () =>
      (await api.get<Envelope<SeasonView>>('/reports/qurbani-season', { params: { eidDate } })).data.data,
    enabled: eidDate !== '',
  });
}
